import type { EvidenceUploadMode } from 'api';
import { addError, logger } from 'monitor';
import { createContext, type ReactNode, useEffect, useState } from 'react';
import { createStore, type StoreApi } from 'zustand';

import { isObjectUrl, revokeObjectUrl } from '../../../files';
import type { Bale, MaterialEvidence } from '../../types';

interface BaleStore {
  bales: Bale[];
  mode: EvidenceUploadMode;
  sopMode: SopModes;
  parentId: string;
  actions: {
    addBale: (bale: Bale) => void;
    removeBale: (baleId: string) => void;
    updateBale: (bale: Bale) => void;
    getBale: (baleId: string) => Bale | undefined;
    updateParentId: (newParentId: string) => void;
  };
}

const BaleAnalysisContext = createContext<StoreApi<BaleStore> | null>(null);

export type SopModes = 'regular' | 'extended';
const DEFAULT_SOP = 'regular';

const BaleAnalysisProvider = ({
  children,
  mode,
  sopMode = DEFAULT_SOP,
  parentId,
}: {
  children: ReactNode;
  mode: EvidenceUploadMode;
  sopMode?: SopModes;
  parentId: string;
}) => {
  const [store] = useState(() =>
    createStore<BaleStore>((set, get) => ({
      actions: {
        addBale: (newBale: Bale) =>
          set((state) => ({
            bales: [...state.bales, newBale],
          })),
        getBale: (baleId: string) => {
          const bale = get().bales.find((bale: Bale) => bale.id === baleId);
          if (!bale) {
            logger.warn(`bale ${baleId} not found`);
          }
          return bale;
        },
        removeBale: (baleId: string) =>
          set((state) => {
            return {
              bales: state.bales.filter((bale) => bale.id !== baleId),
            };
          }),
        updateBale: (updatedBale: Bale) =>
          set((state) => ({
            bales: state.bales.map((bale) => (bale.id === updatedBale.id ? { ...bale, ...updatedBale } : bale)),
          })),

        updateParentId: (newParentId: string) =>
          set({
            parentId: newParentId,
          }),
      },
      bales: [],
      mode,
      parentId,
      sopMode,
    }))
  );

  return <BaleAnalysisContext.Provider value={store}>{children}</BaleAnalysisContext.Provider>;
};

interface EvidenceAnalysisStore {
  evidence: MaterialEvidence[];
  mode: EvidenceUploadMode;
  sopMode: SopModes;
  parentId: string;
  actions: {
    addEvidence: (bale: MaterialEvidence) => void;
    removeEvidence: (baleId: string) => void;
    updateEvidence: (bale: MaterialEvidence) => void;
  };
}

const EvidenceAnalysisContext = createContext<StoreApi<EvidenceAnalysisStore> | null>(null);

const revokeEvidencePhotoUrl = (evidence: MaterialEvidence) => {
  if (isObjectUrl(evidence.photo)) {
    revokeObjectUrl(evidence.photo);
  }
};

const EvidenceAnalysisProvider = ({
  children,
  mode,
  sopMode = DEFAULT_SOP,
  parentId,
}: {
  children: ReactNode;
  mode: EvidenceUploadMode;
  sopMode?: SopModes;
  parentId: string;
}) => {
  const [store] = useState(() =>
    createStore<EvidenceAnalysisStore>((set, get) => ({
      actions: {
        addEvidence: (newEvidence: MaterialEvidence) =>
          set((state) => ({
            evidence: [...state.evidence, newEvidence],
          })),

        removeEvidence: (id: string) => {
          const evidenceToRemove = get().evidence.find((evidence) => evidence.id === id);
          if (!evidenceToRemove) {
            addError(Error(`Evidence ${id} not found`));
            return;
          }
          revokeEvidencePhotoUrl(evidenceToRemove);
          return set((state) => {
            return {
              evidence: state.evidence.filter((bale) => bale.id !== id),
            };
          });
        },
        updateEvidence: (updatedEvidence: MaterialEvidence) => {
          const existingEvidence = get().evidence.find((evidence) => evidence.id === updatedEvidence.id);
          if (!existingEvidence) {
            addError(Error(`Evidence ${updatedEvidence.id} not found`));
            return;
          }

          if (existingEvidence.photo !== updatedEvidence.photo) {
            revokeEvidencePhotoUrl(existingEvidence);
          }

          return set((state) => ({
            evidence: state.evidence.map((evidence) =>
              evidence.id === updatedEvidence.id ? { ...evidence, ...updatedEvidence } : evidence
            ),
          }));
        },
      },
      evidence: [],
      mode,
      parentId,
      sopMode,
    }))
  );

  useEffect(() => {
    return () => {
      store.getState().evidence.forEach((evidence) => {
        revokeEvidencePhotoUrl(evidence);
      });
    };
  }, [store]);

  return <EvidenceAnalysisContext.Provider value={store}>{children}</EvidenceAnalysisContext.Provider>;
};

export { BaleAnalysisContext, BaleAnalysisProvider, type BaleStore, EvidenceAnalysisContext, EvidenceAnalysisProvider };
